import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '@app/core';
import { LearningTrailsCollectionService } from '@app/core/services/learning-trails-collection.service';
import { notNull, selectedEntity, selectedEntityActive } from '@app/shared/utils';
import { SIDENAV_DATA, SIDENAV_REF, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SidenavStudioService } from '../../services/sidenav-studio.service';

@Component({
  templateUrl: './sidenav-learning-studio-collection.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavLearningStudioCollectionComponent implements OnInit {
  private readonly _translateService = inject(TranslateService);
  private readonly store = inject(Store<AppState>);
  private readonly _notificationService = inject(NotificationService);
  private readonly _learningTrailsCollectionService = inject(LearningTrailsCollectionService);
  private readonly _sidenavStudioService = inject(SidenavStudioService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sidenavRef = inject<SidenavRef>(SIDENAV_REF);
  public readonly data = inject(SIDENAV_DATA);
  public form: FormGroup;
  public isCPB = false;
  public submitted$ = new BehaviorSubject<boolean>(false);
  public category$ = new BehaviorSubject<any>(undefined);
  public collection$ = new BehaviorSubject<any>(undefined);

  public ngOnInit(): void {
    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(takeUntilDestroyed(this.destroyRef), notNull())
      .subscribe((user) => {
        this.isCPB = user.editora_id === 1;
        this.initForm(user);
      });
    if (this.data?.id) {
      this.form.patchValue(this.data);
      this.form.get('cpb').setValue(!!this.data.cpb);
      this.form.get('destaque').setValue(!!this.data.destaque);
      if (this.data?.entidades) {
        this.form.get('entidades').setValue(selectedEntity(this.data.entidades));
      }
    } else {
      this.form.get('categoria_id').setValue(this.data.category_id);
    }
  }

  public initForm(user: Observable<any>) {
    this.form = this.formBuilder.group({
      id: undefined,
      categoria_id: [undefined, [Validators.required]],
      entidades: [selectedEntityActive(user), [Validators.required]],
      nome: [undefined, [Validators.required, Validators.maxLength(70), Validators.minLength(3)]],
      destaque: false,
      cpb: false,
      autor: [selectedEntityActive(user)[0].nome, [Validators.maxLength(70), Validators.minLength(3)]],
    });

    this.form.controls.nome.valueChanges.subscribe(() => {
      this.form.controls.nome.markAsTouched();
      this.form.controls.nome.markAsPristine();
    });
  }

  public onClose() {
    this.sidenavRef.close();
  }

  public onSave() {
    if (this.form.valid) {
      if (this.form.value.id) {
        this.update(this.form.value);
      } else {
        this.save();
      }
    } else {
      this.form.markAllAsTouched();
    }
  }

  private save() {
    if (this.form?.valid) {
      this._learningTrailsCollectionService.postCollection(this.form.value).subscribe((resp) => {
        this._sidenavStudioService.insertCollection({
          id: resp['id'],
          categoria_id: this.form.value.categoria_id,
          nome: this.form.value.nome,
          destaque: this.form.value.destaque,
          cpb: this.form.value.cpb,
          autor: this.form.value.autor,
          cursos: [],
          entidades: !!this.data.entidades ? this.data.entidades : [],
          slug: resp['slug'],
        });
        this._notificationService.success(this._translateService.instant('geral.sucesso'));
        this.sidenavRef.close();
      });
    }
  }

  private update(form) {
    if (this.form?.valid) {
      this._learningTrailsCollectionService.updateCollection(form).subscribe(() => {
        this._sidenavStudioService.editOrSaveCollection({
          id: this.form.value.id,
          categoria_id: this.form.value.categoria_id,
          cursos: this.data?.cursos,
          entidades: !!this.data.entidades ? this.data.entidades : [],
          nome: this.form.value.nome,
          slug: this.data?.slug,
          autor: this.form.value.autor,
          destaque: this.form.value.destaque,
          cpb: this.form.value.cpb,
        });
        this._notificationService.success(this._translateService.instant('geral.sucesso'));
        this.sidenavRef.close();
      });
    }
  }
}
